import { money, fmtDate, fmtTime, fmtDateTime, METHOD_LABEL } from "@/lib/format";
import { Crest, ThermalBrand } from "@/components/Brand";

const mask = (p) => (p ? `${p.slice(0, 3)}****${p.slice(-3)}` : "");
const refundLine = (b) =>
  b.refund_amount > 0
    ? b.refund_status === "cash_refunded"
      ? `Cash refund ${money(b.refund_amount)}`
      : b.refund_status === "pending"
        ? `Refund ${money(b.refund_amount)} (gateway pending)`
        : `Refund ${money(b.refund_amount)}`
    : "No refund";

const Info = ({ k, v, mono }) => (
  <div><div className="label-xs">{k}</div><div className={`font-semibold text-slate-900 ${mono ? "font-mono tracking-wider" : ""}`}>{v}</div></div>
);

function A4Receipt({ b }) {
  const t = b.trip || {};
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-rose-200 bg-white" style={{ breakInside: "avoid", marginBottom: 14 }} data-testid={`cancel-receipt-a4-${b.code}`}>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2.5">
            <Crest className="h-11 w-11" />
            <span className="leading-tight"><span className="block font-display text-base font-extrabold text-[#182678]">GPRTU Intercity</span><span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">Developed by Nacasky Co. Ltd</span></span>
          </span>
          <span className="chip border border-rose-200 bg-rose-50 text-rose-700">CANCELLATION RECEIPT</span>
        </div>
        <div className="mt-4 flex items-end gap-4">
          <div><div className="label-xs">From</div><div className="font-display text-xl font-extrabold">{t.origin_city}</div></div>
          <div className="mb-2 flex-1 border-t-2 border-dashed border-rose-200" />
          <div className="text-right"><div className="label-xs">To</div><div className="font-display text-xl font-extrabold">{t.destination_city}</div></div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <Info k="Booking" v={b.code} mono />
          <Info k="Travel date" v={`${fmtDate(t.departure)} ${fmtTime(t.departure)}`} />
          <Info k="Seats" v={b.passengers.map((p) => p.seat).join(", ")} />
          <Info k="Passenger" v={b.passengers.map((p) => p.name).join(", ")} />
          <Info k="Phone" v={mask(b.passengers[0]?.phone)} />
          <Info k="Cancelled on" v={fmtDateTime(b.cancelled_at)} />
          <Info k="Cancelled by" v={`${b.cancelled_by || ""}${b.cancelled_by_role ? ` (${b.cancelled_by_role.replace("_", " ")})` : ""}`} />
          <Info k="Payment" v={METHOD_LABEL[b.payment_method] || b.payment_method} />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 rounded-2xl bg-slate-50 p-4 text-sm">
          <div><div className="label-xs">Ticket total</div><b>{money(b.total)}</b></div>
          <div><div className="label-xs">Charge kept ({b.cancel_percent || 0}%)</div><b className="text-rose-600">{money(b.cancel_fee || 0)}</b></div>
          <div><div className="label-xs">Refund</div><b className="text-emerald-700">{money(b.refund_amount || 0)}</b></div>
          <div className="col-span-3 text-xs text-slate-500">{refundLine(b)}. {b.cancel_reason || ""}</div>
        </div>
        <div className="mt-3 text-center text-[11px] text-slate-400">This receipt confirms the ticket above has been cancelled. Keep it for your records.</div>
      </div>
    </div>
  );
}

function ThermalReceipt({ b }) {
  const t = b.trip || {};
  return (
    <div className="thermal">
      <ThermalBrand />
      <div className="center big">CANCELLATION</div>
      <hr />
      <div className="center">{t.origin_city} &gt; {t.destination_city}</div>
      <div className="row"><span>Booking</span><span>{b.code}</span></div>
      <div className="row"><span>Travel</span><span>{fmtDate(t.departure)} {fmtTime(t.departure)}</span></div>
      <div className="row"><span>Seats</span><span>{b.passengers.map((p) => p.seat).join(", ")}</span></div>
      <div className="row"><span>Passenger</span><span>{b.passengers.map((p) => p.name).join(", ")}</span></div>
      <hr />
      <div className="row"><span>Ticket total</span><span>{money(b.total)}</span></div>
      <div className="row"><span>Charge ({b.cancel_percent || 0}%)</span><span>{money(b.cancel_fee || 0)}</span></div>
      <div className="row big"><span>REFUND</span><span>{money(b.refund_amount || 0)}</span></div>
      <div className="center">{refundLine(b)}</div>
      <hr />
      <div className="row"><span>Cancelled</span><span>{fmtDateTime(b.cancelled_at)}</span></div>
      <div className="row"><span>By</span><span>{b.cancelled_by}</span></div>
      {b.cancel_reason && <div className="center">{b.cancel_reason}</div>}
      <hr />
      <div className="center">Keep this receipt for your records.</div>
      <hr />
    </div>
  );
}

export default function CancelReceiptView({ booking, format = "a4" }) {
  if (format === "thermal") return <ThermalReceipt b={booking} />;
  return <div className="a4-list"><A4Receipt b={booking} /></div>;
}
